import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", purple:"#7c3aed", orange:"#e67e22",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

// ★ Top-level kind — owner asked to separate Services (bookable treatments)
//   from Retail (products). The categories below carry a `kind` so the page
//   tab filter can split them cleanly. Retail categories follow the
//   "Retail — Brand" naming convention used in the DB.
const CATEGORIES = [
  // Specials FIRST — appears at the top of the public booking page with a
  // "🔥 LIMITED TIME" badge. Use this for promo / monthly offers.
  { id:"Specials",             color:"#e11d48", icon:"🔥", kind:"service" },
  // Services (bookable as appointments)
  { id:"Injectables",         color:"#b48c3c", icon:"💉", kind:"service" },
  { id:"Facials",              color:"#e67e22", icon:"✨", kind:"service" },
  { id:"Skin Treatments",      color:"#2563eb", icon:"🌟", kind:"service" },
  { id:"Laser Hair Removal",   color:"#db2777", icon:"🪒", kind:"service" },
  { id:"Laser Treatments",     color:"#c0392b", icon:"⚡", kind:"service" },
  { id:"Health + Wellness",    color:"#2e8b57", icon:"💊", kind:"service" },
  { id:"Weightloss",           color:"#16a085", icon:"⚖️", kind:"service" },
  { id:"Hair Restoration",     color:"#8e44ad", icon:"💇", kind:"service" },
  { id:"Treatment Add-ons",    color:"#7c3aed", icon:"➕", kind:"service" },
  { id:"Memberships",          color:"#d35400", icon:"⭐", kind:"service" },
  // Retail (sellable products — not bookable as appointment slots)
  { id:"Retail — Hydrinity",   color:"#0ea5e9", icon:"🧴", kind:"retail" },
  { id:"Retail — IS Clinical", color:"#10b981", icon:"🧪", kind:"retail" },
  { id:"Retail — Avinichi",    color:"#f59e0b", icon:"💎", kind:"retail" },
];

const SERVICE_CATEGORIES = CATEGORIES.filter(c => c.kind === "service");
const RETAIL_CATEGORIES  = CATEGORIES.filter(c => c.kind === "retail");
// Heuristic for DB rows whose category doesn't match exactly: any category
// starting with "Retail" is treated as retail.
const isRetailCategory = (cat) => /^retail/i.test(String(cat || "").trim());

const inp = {width:"100%",padding:"10px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:8,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box"};

export function ServicesManager() {
  const [services, setServices] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filterCat, setFilterCat] = useState("all");
  const [loading, setLoading] = useState(true);
  // New: search box + view toggle so this screen stops feeling like a wall
  // of cards and starts feeling like a real menu editor.
  const [search, setSearch] = useState("");
  const [view, setView] = useState("cards"); // 'cards' | 'list'
  const [showInactive, setShowInactive] = useState(false);
  // ★ Top-level mode — owner asked to separate the appointment-bookable
  //   services from retail products. Switching this re-scopes the category
  //   chips AND the search/filtered list so neither half pollutes the other.
  const [mode, setMode] = useState("service"); // 'service' | 'retail'

  useEffect(() => {
    load();
    const ch = supabase.channel("services-sync")
      .on("postgres_changes", { event: "*", schema: "public", table: "services" }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  const load = async () => {
    const { data } = await supabase.from("services").select("*").order("category").order("order_index").order("name");
    setServices(data || []);
    setLoading(false);
  };

  const addNew = () => {
    setSelected({ name:"", category:"Injectables", price:0, duration_minutes:60, description:"", active:true, requires_deposit:false, deposit_amount:0 });
    setShowForm(true);
  };

  const edit = (s) => {
    setSelected(s);
    setShowForm(true);
  };

  const toggleActive = async (s) => {
    // Confirm only when HIDING. Showing back is harmless.
    if (s.active && !window.confirm(`Hide "${s.name}"?\n\nIt will disappear from the booking and checkout pickers. Existing appointments and packages keep working. Toggle "Show hidden" at the top to bring it back.`)) {
      return;
    }
    await supabase.from("services").update({ active: !s.active }).eq("id", s.id);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this service? Existing appointments won't be affected.")) return;
    await supabase.from("services").delete().eq("id", id);
  };

  const save = async () => {
    if (!selected.name || !selected.category) { alert("Name and category required"); return; }
    const data = {...selected};
    delete data.created_at;

    if (selected.id) {
      // Update existing — strip id from the payload (used only in the WHERE)
      delete data.id;
      const { error } = await supabase.from("services").update(data).eq("id", selected.id);
      if (error) { alert("Save failed: " + error.message); return; }
    } else {
      // Insert new — supply our own id because the services table doesn't
      // have a DB sequence (legacy schema). Without this, inserts silently
      // fail or duplicate-key when the next user adds something at the same
      // millisecond. Date.now() + random nudge is collision-safe enough.
      delete data.id;
      const newId = Date.now() + Math.floor(Math.random() * 1000);
      const { error } = await supabase.from("services").insert([{ id: newId, ...data }]);
      if (error) { alert("Create failed: " + error.message); return; }
    }
    setShowForm(false);
    setSelected(null);
    load();   // explicit reload — don't rely solely on realtime sync
  };

  // Filter pipeline: mode (Services/Retail) → category → active toggle → search
  const inActiveMode = (s) => mode === "retail" ? isRetailCategory(s.category) : !isRetailCategory(s.category);
  const filtered = services.filter(s => {
    if (!inActiveMode(s)) return false;
    if (filterCat !== "all" && s.category !== filterCat) return false;
    if (!showInactive && !s.active) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${s.name || ""} ${s.description || ""} ${s.category || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  // Category chips for the current mode only
  const modeCategories = mode === "retail" ? RETAIL_CATEGORIES : SERVICE_CATEGORIES;
  // Counts (mode-scoped — chips don't show services-mode totals when on retail)
  const modeServices = services.filter(inActiveMode);
  const byCategory = {};
  modeCategories.forEach(cat => {
    byCategory[cat.id] = filtered.filter(s => s.category === cat.id);
  });

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:10}}>
        <div>
          <div style={{fontSize:22,fontWeight:800,color:C.gold,fontFamily:S}}>Services & Retail</div>
          <div style={{fontSize:12,color:C.muted,marginTop:2}}>
            {mode === "retail" ? "🛒 Retail / products" : "🧴 Bookable services"} · {modeServices.filter(s=>s.active).length} active · {modeServices.filter(s=>!s.active).length} hidden · {filtered.length} showing
          </div>
        </div>
        <button
          onClick={() => { setSelected({
            name:"", category: mode === "retail" ? "Retail — Hydrinity" : "Injectables",
            price:0, duration_minutes: mode === "retail" ? 0 : 60,
            description:"", active:true, requires_deposit:false, deposit_amount:0,
          }); setShowForm(true); }}
          style={{padding:"10px 18px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F}}
        >
          {mode === "retail" ? "+ Add Product" : "+ Add Service"}
        </button>
      </div>

      {/* ★ Services vs Retail — top-level mode switcher. Switching resets
          the category chip so the user lands cleanly on the new view. */}
      <div style={{display:"flex",gap:8,marginBottom:14,background:C.light,padding:4,borderRadius:10,border:"1px solid "+C.border,maxWidth:480}}>
        {[
          { k:"service", label:"🧴 Services",        sub:"Bookable treatments" },
          { k:"retail",  label:"🛒 Retail / Products", sub:"Skincare lines & products" },
        ].map(t => {
          const isActive = mode === t.k;
          const count = services.filter(s => t.k === "retail" ? isRetailCategory(s.category) : !isRetailCategory(s.category)).length;
          return (
            <button
              key={t.k}
              onClick={() => { setMode(t.k); setFilterCat("all"); }}
              style={{
                flex:1, padding:"10px 14px",
                background: isActive ? "#fff" : "transparent",
                color: isActive ? C.gold : C.muted,
                border: "none", borderRadius: 7,
                fontSize: 12.5, fontWeight: 800, cursor: "pointer",
                fontFamily: F, textAlign: "left",
                boxShadow: isActive ? "0 2px 6px rgba(0,0,0,0.06)" : "none",
              }}
            >
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:6}}>
                <span>{t.label}</span>
                <span style={{fontSize:10,opacity:0.7,fontWeight:600}}>{count}</span>
              </div>
              <div style={{fontSize:9.5,fontWeight:500,color:C.muted,marginTop:1,letterSpacing:0.2}}>{t.sub}</div>
            </button>
          );
        })}
      </div>

      {/* Toolbar: search + view toggle + show-hidden toggle. Stays sticky-looking. */}
      <div style={{display:"flex",gap:8,marginBottom:14,alignItems:"center",flexWrap:"wrap"}}>
        <div style={{position:"relative",flex:"1 1 220px",maxWidth:380}}>
          <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",fontSize:14,color:C.muted}}>🔍</span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, description, or category..."
            style={{...inp, paddingLeft:32}}
          />
        </div>
        <div style={{display:"flex",background:C.light,borderRadius:8,padding:3,gap:2,border:"1px solid "+C.border}}>
          <button onClick={()=>setView("cards")} style={{padding:"7px 14px",background:view==="cards"?"#fff":"transparent",color:view==="cards"?C.gold:C.muted,border:"none",borderRadius:6,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F,boxShadow:view==="cards"?"0 1px 2px rgba(0,0,0,0.06)":"none"}}>🗂 Cards</button>
          <button onClick={()=>setView("list")}  style={{padding:"7px 14px",background:view==="list" ?"#fff":"transparent",color:view==="list" ?C.gold:C.muted,border:"none",borderRadius:6,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F,boxShadow:view==="list" ?"0 1px 2px rgba(0,0,0,0.06)":"none"}}>≡ List</button>
        </div>
        <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:C.muted,cursor:"pointer"}}>
          <input type="checkbox" checked={showInactive} onChange={e=>setShowInactive(e.target.checked)} />
          Show hidden
        </label>
      </div>

      {/* Category chips — scoped to the current mode (Services or Retail) */}
      <div style={{display:"flex",gap:6,marginBottom:20,flexWrap:"wrap"}}>
        <button onClick={()=>setFilterCat("all")} style={{padding:"7px 14px",background:filterCat==="all"?"linear-gradient(135deg,"+C.gold+","+C.goldD+")":C.light,color:filterCat==="all"?"#fff":C.text,border:"1px solid "+(filterCat==="all"?C.gold:C.border),borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:F}}>All ({modeServices.length})</button>
        {modeCategories.map(cat => {
          const count = modeServices.filter(s => s.category === cat.id).length;
          if (count === 0) return null;
          return (
            <button key={cat.id} onClick={()=>setFilterCat(cat.id)} style={{padding:"7px 14px",background:filterCat===cat.id?cat.color:C.light,color:filterCat===cat.id?"#fff":C.text,border:"1px solid "+(filterCat===cat.id?cat.color:C.border),borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:F}}>{cat.icon} {cat.id} ({count})</button>
          );
        })}
      </div>

      {loading && <div style={{padding:40,textAlign:"center",color:C.muted}}>Loading...</div>}

      {!loading && services.length === 0 && (
        <div style={{padding:60,textAlign:"center",background:C.light,borderRadius:12,border:"1px dashed "+C.border}}>
          <div style={{fontSize:40,marginBottom:10,opacity:0.3}}>🌟</div>
          <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:6}}>No services yet</div>
          <div style={{fontSize:12,color:C.muted,marginBottom:14}}>Add your first service!</div>
          <button onClick={addNew} style={{padding:"9px 16px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>+ Add Service</button>
        </div>
      )}

      {/* LIST VIEW — compact table for power-editing many services. */}
      {view === "list" && filtered.length > 0 && (
        <div style={{background:"#fff",border:"1px solid "+C.border,borderRadius:10,overflow:"hidden"}}>
          <div style={{display:"grid",gridTemplateColumns:"minmax(200px,2fr) 120px 90px 80px 100px 160px",gap:0,padding:"10px 14px",background:C.light,fontSize:10,fontWeight:800,color:C.muted,textTransform:"uppercase",letterSpacing:0.6,borderBottom:"1px solid "+C.border}}>
            <div>Service</div>
            <div>Category</div>
            <div style={{textAlign:"right"}}>Price</div>
            <div style={{textAlign:"right"}}>Duration</div>
            <div style={{textAlign:"center"}}>Deposit</div>
            <div style={{textAlign:"right"}}>Actions</div>
          </div>
          {filtered.map(s => {
            const cat = CATEGORIES.find(c=>c.id===s.category) || {color:C.gold,icon:"•"};
            return (
              <div key={s.id} style={{display:"grid",gridTemplateColumns:"minmax(200px,2fr) 120px 90px 80px 100px 160px",gap:0,padding:"12px 14px",borderBottom:"1px solid "+C.border,alignItems:"center",borderLeft:"3px solid "+cat.color,opacity:s.active?1:0.55}}>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:C.text}}>{s.name}{!s.active && <span style={{marginLeft:8,fontSize:9,padding:"1px 6px",background:C.muted,color:"#fff",borderRadius:8,fontWeight:700}}>HIDDEN</span>}</div>
                  {s.description && <div style={{fontSize:10,color:C.muted,marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.description}</div>}
                </div>
                <div style={{fontSize:11,color:cat.color,fontWeight:600}}>{cat.icon} {s.category}</div>
                <div style={{fontSize:13,fontWeight:700,color:cat.color,textAlign:"right"}}>{s.price > 0 ? "$"+s.price : <span style={{fontSize:10,color:C.muted,fontStyle:"italic"}}>Quote</span>}</div>
                <div style={{fontSize:12,color:C.muted,textAlign:"right"}}>{s.duration_minutes} min</div>
                <div style={{textAlign:"center",fontSize:11,color:s.requires_deposit?C.green:C.muted,fontWeight:s.requires_deposit?700:500}}>{s.requires_deposit && s.deposit_amount>0 ? `$${s.deposit_amount}` : "—"}</div>
                <div style={{display:"flex",gap:4,justifyContent:"flex-end"}}>
                  <button onClick={()=>edit(s)} style={{padding:"6px 10px",background:C.goldBg,color:C.gold,border:"1px solid "+C.borderGold,borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>Edit</button>
                  <button onClick={()=>toggleActive(s)} style={{padding:"6px 10px",background:s.active?C.light:C.greenL,color:s.active?C.muted:C.green,border:"1px solid "+(s.active?C.border:C.green+"40"),borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>{s.active?"Hide":"Show"}</button>
                  <button onClick={()=>remove(s.id)} style={{padding:"6px 8px",background:C.redL,color:C.red,border:"1px solid "+C.red+"30",borderRadius:6,fontSize:11,cursor:"pointer",fontFamily:F}}>🗑</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* CARD VIEW — visual menu grouped by category (the original layout).
          Now scoped to the active mode (Services or Retail). */}
      {view === "cards" && modeCategories.map(cat => {
        const catServices = byCategory[cat.id];
        if (!catServices || catServices.length === 0) return null;
        return (
          <div key={cat.id} style={{marginBottom:24}}>
            <div style={{fontSize:13,fontWeight:800,color:cat.color,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:10,paddingBottom:6,borderBottom:"2px solid "+cat.color+"30"}}>
              {cat.icon} {cat.id}
              <span style={{fontSize:11,color:C.muted,marginLeft:8,fontWeight:500}}>({catServices.length})</span>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:10}}>
              {catServices.map(s => (
                <div key={s.id} style={{background:C.card,border:"1px solid "+C.border,borderLeft:"3px solid "+cat.color,borderRadius:10,padding:14,opacity:s.active?1:0.5,position:"relative"}}>
                  {!s.active && <div style={{position:"absolute",top:8,right:8,fontSize:9,padding:"2px 7px",background:C.muted,color:"#fff",borderRadius:10,fontWeight:700}}>INACTIVE</div>}
                  <div style={{fontSize:14,fontWeight:800,color:C.text,fontFamily:S,marginBottom:4,paddingRight:s.active?0:70}}>{s.name}</div>
                  {s.description && <div style={{fontSize:11,color:C.muted,marginBottom:8,lineHeight:1.4}}>{s.description}</div>}
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",paddingTop:8,borderTop:"1px solid "+C.border}}>
                    <div>
                      {s.price > 0 ? <div style={{fontSize:16,fontWeight:800,color:cat.color}}>${s.price}{s.price_max && s.price_max > s.price ? "-$"+s.price_max : ""}</div> : <div style={{fontSize:11,color:C.muted,fontStyle:"italic"}}>Call for pricing</div>}
                    </div>
                    <div style={{fontSize:11,color:C.muted,fontWeight:600}}>{s.duration_minutes} min</div>
                  </div>
                  {s.requires_deposit && s.deposit_amount > 0 && (
                    <div style={{marginTop:6,padding:"5px 8px",background:"rgba(46,139,87,0.12)",border:"1px solid rgba(46,139,87,0.3)",borderRadius:6,fontSize:10,color:"#2e8b57",fontWeight:700,textAlign:"center"}}>💳 ${s.deposit_amount} deposit required</div>
                  )}
                  <div style={{display:"flex",gap:4,marginTop:10}}>
                    <button onClick={()=>edit(s)} style={{flex:1,padding:"6px",background:C.goldBg,color:C.gold,border:"1px solid "+C.borderGold,borderRadius:6,fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:F}}>Edit</button>
                    <button onClick={()=>toggleActive(s)} style={{flex:1,padding:"6px",background:s.active?C.light:C.greenL,color:s.active?C.muted:C.green,border:"1px solid "+(s.active?C.border:C.green+"40"),borderRadius:6,fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:F}}>{s.active?"Hide":"Show"}</button>
                    <button onClick={()=>remove(s.id)} style={{padding:"6px 10px",background:C.redL,color:C.red,border:"1px solid "+C.red+"30",borderRadius:6,fontSize:10,cursor:"pointer",fontFamily:F}}>🗑</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {showForm && selected && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)"}}>
          <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:500,padding:24,maxHeight:"90vh",overflowY:"auto",position:"relative"}}>
            <button onClick={()=>{setShowForm(false);setSelected(null);}} style={{position:"absolute",top:12,right:12,background:C.light,border:"none",borderRadius:7,padding:"4px 10px",cursor:"pointer",fontSize:16,color:C.muted}}>×</button>
            
            <div style={{fontSize:18,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:16}}>{selected.id?"Edit Service":"Add Service"}</div>

            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div>
                <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Service Name *</label>
                <input value={selected.name} onChange={e=>setSelected({...selected,name:e.target.value})} placeholder="e.g. Botox" style={inp}/>
              </div>
              <div>
                <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Category *</label>
                <select value={selected.category} onChange={e=>setSelected({...selected,category:e.target.value})} style={inp}>
                  <optgroup label="🧴 Services">
                    {SERVICE_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.icon} {c.id}</option>)}
                  </optgroup>
                  <optgroup label="🛒 Retail / Products">
                    {RETAIL_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.icon} {c.id}</option>)}
                  </optgroup>
                </select>
              </div>
              <div>
                <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Description (shown on booking)</label>
                <textarea value={selected.description||""} onChange={e=>setSelected({...selected,description:e.target.value})} rows={2} placeholder="Brief description for clients..." style={{...inp,resize:"vertical"}}/>
              </div>
              <div style={{padding:12,background:"rgba(124,58,237,0.08)",border:"1px solid rgba(124,58,237,0.3)",borderRadius:10}}>
                <label style={{fontSize:10,fontWeight:700,color:"#7c3aed",textTransform:"uppercase",display:"block",marginBottom:5,display:"flex",alignItems:"center",gap:6}}>
                  🤖 Notes for the AI bot (Sophia)
                </label>
                <textarea
                  value={selected.bot_notes || ""}
                  onChange={e=>setSelected({...selected,bot_notes:e.target.value})}
                  rows={5}
                  placeholder={"Tell Sophia what this treatment is, what concerns it solves, what's included, candidates, downtime, etc.\n\nExample for Morpheus8:\n• RF microneedling for skin tightening + collagen\n• Best for sagging skin, fine lines, acne scars\n• 3-session package recommended\n• 3-5 day redness/downtime\n• Not for active acne or recent fillers"}
                  style={{...inp,resize:"vertical",minHeight:110,fontFamily:F}}
                />
                <div style={{fontSize:10,color:C.muted,marginTop:5,lineHeight:1.4}}>
                  💡 The bot reads these notes whenever a client asks about this service OR describes a concern that matches the keywords here. Be specific about what the treatment solves — that's how the bot will recommend it when someone says "I want something for tight skin" or "help with acne scars".
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
                <div>
                  <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Price ($)</label>
                  <input type="number" value={selected.price||0} onChange={e=>setSelected({...selected,price:+e.target.value})} style={inp}/>
                </div>
                <div>
                  <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Max Price ($)</label>
                  <input type="number" value={selected.price_max||""} onChange={e=>setSelected({...selected,price_max:e.target.value?+e.target.value:null})} placeholder="optional" style={inp}/>
                </div>
                <div>
                  <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Duration (min)</label>
                  <input type="number" value={selected.duration_minutes||60} onChange={e=>setSelected({...selected,duration_minutes:+e.target.value})} style={inp}/>
                </div>
              </div>
              <div style={{padding:14,background:"rgba(46,139,87,0.08)",border:"1px solid rgba(46,139,87,0.25)",borderRadius:10}}>
                <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",marginBottom:selected.requires_deposit?10:0}}>
                  <input type="checkbox" checked={selected.requires_deposit||false} onChange={e=>setSelected({...selected,requires_deposit:e.target.checked})} style={{accentColor:"#2e8b57",width:16,height:16}}/>
                  <span style={{fontSize:13,color:C.text,fontWeight:700}}>💳 Require deposit to book online</span>
                </label>
                {selected.requires_deposit && (
                  <div>
                    <label style={{fontSize:10,fontWeight:700,color:"#2e8b57",textTransform:"uppercase",display:"block",marginBottom:5}}>Deposit Amount ($)</label>
                    <input type="number" value={selected.deposit_amount||0} onChange={e=>setSelected({...selected,deposit_amount:+e.target.value})} placeholder="50" style={{...inp,fontWeight:700}}/>
                    <div style={{fontSize:10,color:C.muted,marginTop:4,fontStyle:"italic"}}>Customers must pay this amount at checkout to confirm their booking</div>
                  </div>
                )}
              </div>
              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"10px 14px",background:C.light,borderRadius:8}}>
                <input type="checkbox" checked={selected.requires_consultation||false} onChange={e=>setSelected({...selected,requires_consultation:e.target.checked})} style={{accentColor:C.gold}}/>
                <span style={{fontSize:12,color:C.text,fontWeight:600}}>Requires consultation before booking</span>
              </label>
              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"10px 14px",background:C.light,borderRadius:8}}>
                <input type="checkbox" checked={selected.hide_price||false} onChange={e=>setSelected({...selected,hide_price:e.target.checked})} style={{accentColor:C.gold}}/>
                <span style={{fontSize:12,color:C.text,fontWeight:600}}>🔒 Hide price on booking page (shows "Call for pricing")</span>
              </label>
              <label style={{display:"flex",alignItems:"flex-start",gap:8,cursor:"pointer",padding:"10px 14px",background:selected.new_patient_only?C.goldBg:C.light,border:"1.5px solid "+(selected.new_patient_only?C.gold:"transparent"),borderRadius:8}}>
                <input type="checkbox" checked={selected.new_patient_only||false} onChange={e=>setSelected({...selected,new_patient_only:e.target.checked})} style={{accentColor:C.gold,marginTop:2}}/>
                <div>
                  <div style={{fontSize:12,color:C.text,fontWeight:700}}>🆕 New Patient Special</div>
                  <div style={{fontSize:11,color:C.muted,marginTop:2,lineHeight:1.4}}>Only show this price to first-time clients. Returning clients will see the standard service price instead. The AI bot enforces this automatically.</div>
                </div>
              </label>
              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"10px 14px",background:selected.active?C.greenL:C.light,borderRadius:8}}>
                <input type="checkbox" checked={selected.active} onChange={e=>setSelected({...selected,active:e.target.checked})} style={{accentColor:C.gold}}/>
                <span style={{fontSize:12,color:C.text,fontWeight:600}}>Active (visible on booking link)</span>
              </label>
            </div>

            <div style={{display:"flex",gap:8,marginTop:18,justifyContent:"flex-end"}}>
              <button onClick={()=>{setShowForm(false);setSelected(null);}} style={{padding:"9px 16px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:F}}>Cancel</button>
              <button onClick={save} style={{padding:"9px 16px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>{selected.id?"Save":"Create"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
